// ============================================================
// ROSTER PROJECTIONS - turn a Combatant into what the engine eats.
//
// The combat engine consumes `Target` and `Slot[]`. Before the roster
// those were one-way SNAPSHOTS taken at spawn (characterToTarget /
// characterToSlots), so a character's sheet and its combat presence
// could silently drift apart. Here they become live PROJECTIONS:
// recomputed from the record, never stored.
//
// PERFORMANCE is the design constraint. resolveDefenderSheet walks the
// talent and trait catalogs, so it must not run on every wound tick.
// The sheet/state split does double duty: the expensive half is a pure
// function of the Character and is memoized on OBJECT IDENTITY, and the
// cheap half is a field overlay.
//
// That caching is sound only while a Character is never mutated in
// place. It isn't: CharacterEditor edits a draft copy, normalizeCharacter
// is read-only, combat only ever writes CombatState, and
// combatantFromCharacter deep-clones on the way in. A sheet edit
// produces a NEW object, so invalidation is automatic. See
// code-conventions.md.
// ============================================================
import { sideOf } from './team';
import { effectiveSheet } from './stancedSheet';
import type { Armour, Cover, Slot, Target } from '../../types';
import type { AttackerCombatStats, Character, CombatWeapon, DefenderSheet } from '../character/types';
import type { Combatant, KillTeamState } from './types';
import { resolveAttackerStats, resolveCombatWeapon, resolveDefenderSheet } from '../character/combatStatsResolver';
import type { CoverConflictMode } from '../coverConflict';
import { applyDrainOverlay, readDrains } from '../statDrains';
import type { VehicleFacing } from '../vehicleFacing';
import { changesNothing, readMap, readingKey, wearReading } from './mapReading';
import type { SurfaceLike } from './mapReading';

export type { SurfaceLike } from './mapReading';

// The loadout used to be a fixed 40-cell grid (`LOADOUT_SLOTS`), padded with
// empty slots. It is a LIST now - exactly as long as the character is armed,
// with no ceiling. See `loadoutOrder` below and dev-docs/decisions-log.md.

// ────────────────────────────────────────────────────────────
// Caches - keyed on Character / Combatant object identity
// ────────────────────────────────────────────────────────────

const defenderSheetCache = new WeakMap<Character, DefenderSheet>();
const attackerStatsCache = new WeakMap<Character, Map<string, AttackerCombatStats>>();
const combatWeaponCache = new WeakMap<Character, Map<string, CombatWeapon | null>>();

interface ProjectionEntry {
  sheet: Character;
  overrides: object;
  state: object;
  name: string;
  out: Target;
}
const defenderProjectionCache = new WeakMap<Combatant, ProjectionEntry>();

/**
 * The rack, keyed on what it is actually made of.
 *
 * Keyed on the SHEET, not the Combatant. A wound tick spreads a new
 * Combatant and a new CombatState, so a Combatant-keyed WeakMap would miss
 * on exactly the case this exists for - and `WeaponPlate` is memoised on the
 * slot, so every hit would re-render the whole rack. `sheet`, `weaponAmmo`
 * and `slotOrder` all survive that spread untouched, and they are the only
 * three things that can change what a plate looks like.
 */
interface LoadoutEntry {
  weaponAmmo: object | undefined;
  slotOrder: unknown;
  /** Stat drains reach skillValue/psyRating, so the key includes them (v8.290.0). */
  conditions: unknown;
  out: { slots: Slot[]; weaponIds: string[] };
}
const loadoutProjectionCache = new WeakMap<Character, LoadoutEntry>();

/**
 * Second level, hung off the base projection: cover key → the Target
 * wearing it. Keyed on the BASE object so it is discarded the moment the
 * combatant changes and the base is rebuilt.
 */
const derivedProjectionCache = new WeakMap<Target, Map<string, Target>>();

/** resolveDefenderSheet, memoized on Character identity. */
export function cachedDefenderSheet(character: Character): DefenderSheet {
  const hit = defenderSheetCache.get(character);
  if (hit) return hit;
  const sheet = resolveDefenderSheet(character);
  defenderSheetCache.set(character, sheet);
  return sheet;
}

/** resolveAttackerStats, memoized on (Character identity, weaponId). */
export function cachedAttackerStats(character: Character, weaponId: string): AttackerCombatStats {
  let byWeapon = attackerStatsCache.get(character);
  if (!byWeapon) {
    byWeapon = new Map();
    attackerStatsCache.set(character, byWeapon);
  }
  const hit = byWeapon.get(weaponId);
  if (hit) return hit;
  const stats = resolveAttackerStats(character, weaponId);
  byWeapon.set(weaponId, stats);
  return stats;
}

/** resolveCombatWeapon, memoized on (Character identity, weaponId). */
export function cachedCombatWeapon(character: Character, weaponId: string): CombatWeapon | null {
  let byWeapon = combatWeaponCache.get(character);
  if (!byWeapon) {
    byWeapon = new Map();
    combatWeaponCache.set(character, byWeapon);
  }
  if (byWeapon.has(weaponId)) return byWeapon.get(weaponId) ?? null;
  const weapon = resolveCombatWeapon(character, weaponId);
  byWeapon.set(weaponId, weapon);
  return weapon;
}

// ────────────────────────────────────────────────────────────
// Defender projection
// ────────────────────────────────────────────────────────────

/** The pristine, sheet-derived AP - what armour looked like before combat ate it. */
export function sheetArmour(c: Combatant): Armour {
  return { ...(c.overrides.armour ?? cachedDefenderSheet(effectiveSheet(c)).armour) };
}

/**
 * Combatant → the `Target` the engine already consumes.
 *
 * Deliberately does NOT fold state.defenderCustomMods into toughnessBonus:
 * the pre-roster bridge passed no custom mods either, and defence-time
 * application (engine/defenceCalc.applyDefenderMods) stays the only path.
 * Changing that is a rules change, not a refactor.
 *
 * v7.51.4: that WAS the only path in the forecast and in nothing else -
 * the live roll folded them nowhere, so a defender modifier showed in the
 * plot and never in the dice. `withDefenderMods` is now applied where the
 * defence target is resolved. This projection still does not fold them,
 * which is what keeps the two from doubling.
 */
export function projectDefender(c: Combatant): Target {
  const hit = defenderProjectionCache.get(c);
  if (hit && sameDefenderInputs(hit, c)) return hit.out;

  // The stat-drain overlay, LAST (v8.290.0): dodge/parry lose the full
  // drain, bonus-based stats a tenth, psy its rating points - see
  // engine/statDrains.ts for the ADR'd semantics. Identity-preserving
  // when nothing drains, so the cache below stays honest.
  const drained = applyDrainOverlay(defenderTarget(c));

  defenderProjectionCache.set(c, {
    sheet: c.sheet,
    overrides: c.overrides,
    state: c.state,
    name: c.name,
    out: drained,
  });
  return drained;
}

/** Is the cached projection still made of exactly these parts? */
function sameDefenderInputs(hit: ProjectionEntry, c: Combatant): boolean {
  return hit.sheet === c.sheet && hit.overrides === c.overrides && hit.state === c.state && hit.name === c.name;
}

/**
 * The sheet with the live state laid over it, before any drain.
 *
 * Tolerant of a PARTIAL state: an undo snapshot written by an older
 * client (or a hand-built one in a test) may omit fields, and a
 * projection must never throw on the restore path.
 */
function defenderTarget(c: Combatant): Target {
  // The EFFECTIVE sheet: the stored one, or - with a stance up - the one that wears its effects.
  const { woundsMax, isHorde, hordeMaxMagnitude, ...rest } = {
    ...cachedDefenderSheet(effectiveSheet(c)),
    ...c.overrides,
  };
  const state = c.state;
  return {
    ...rest,
    name: c.name,
    traits: [],
    wounds: { current: state.woundsCurrent ?? 0, max: woundsMax },
    conditions: [...(state.conditions ?? [])],
    criticalDamage: [...(state.criticalDamage ?? [])],
    armour: { ...(state.armour ?? rest.armour) },
    armourOriginal: { ...rest.armour },
    forceField: { ...(state.forceField ?? rest.forceField) },
    horde: {
      isHorde,
      magnitude: state.hordeMagnitude ?? 0,
      maxMagnitude: hordeMaxMagnitude,
      ...(state.hordeBroken ? { broken: true } : {}),
    },
    cover: { ...(state.cover ?? { level: 'none', ap: 0 }) },
    ...roundState(state),
    // The team tag, for an exposure's `@team` scope (v8.349.0), and the
    // side, for `@hostile` / `@ally` (2026-10-23). A blank tag is not a
    // team (`teamOf`'s rule), so it has no side either.
    ...(c.sheet.team?.tag?.trim() ? { teamTag: c.sheet.team.tag, sideTag: sideOf(c.sheet.team) } : {}),
  } as unknown as Target;
}

/** The per-round choices and flags the state carries, copied across. */
function roundState(state: Combatant['state']) {
  return {
    roundCountdown: !!state.roundCountdown,
    countdownTickedThisRound: !!state.countdownTickedThisRound,
    defenderCustomMods: [...(state.defenderCustomMods ?? [])],
    defensiveStance: state.defensiveStance,
    frenzied: state.frenzied,
    vehicleFacing: state.vehicleFacing,
    ...(state.standingReaction ? { standingReaction: state.standingReaction } : {}),
    // Carried so that the legacy write-back, which rebuilds the state from the Target, keeps them.
    ...(state.activeStances?.length ? { activeStances: [...state.activeStances] } : {}),
    ...(state.killTeam ? { killTeam: copyKillTeam(state.killTeam) } : {}),
    // What he has faced (engine/exposureDread): wiped by every legacy write until 2026-10-24, so a horror asked again.
    ...(state.dreadTested ? { dreadTested: { ...state.dreadTested } } : {}),
  };
}

/** Squad Mode, copied (its joined list too): the projection never shares an array with the stored state. */
const copyKillTeam = (k: KillTeamState): KillTeamState => ({ ...k, ...(k.joined ? { joined: [...k.joined] } : {}) });

/**
 * The same Target, wearing the cover THIS attacker's line of sight sees.
 *
 * Cover was never a property of the defender. It is a relation between
 * two tokens and the geometry between them - two attackers on opposite
 * sides of a barricade face different answers about the same person.
 * Storing it on `state.cover` meant one shared value, so the second
 * derivation silently replaced the first and a third party read whichever
 * client wrote last. That is not a race to arbitrate; it is a modelling
 * error, and the fix is to stop writing it down.
 *
 * A HAND-SET COVER WINS. A person's ruling outranks the map, and that is
 * what "pinning" a value means: it is written to `state.cover` as manual
 * and the map stops answering for that combatant until it is cleared.
 * (`level: 'none'` is not a ruling - it is the absence of one - so the
 * map still fills it.)
 *
 * The result is stamped `source: 'derived'`, which is not a return of the
 * old expiry marker but its opposite: it is how `stateFromTarget` knows
 * to drop this value rather than persist it, so a round trip through
 * `updateTab` cannot quietly turn what the map is saying into what a
 * person decided.
 *
 * Memoised on top of the identity cache `projectDefender` already keeps,
 * keyed by the cover itself, so an unchanged derivation returns the same
 * object and nothing downstream rerenders.
 */
export function projectDefenderFrom(
  c: Combatant,
  derivedCover?: Cover | null,
  surface?: SurfaceLike | null,
  coverMode: CoverConflictMode = 'highest',
  /**
   * What the MAP says this vehicle presents, for THIS attacker.
   *
   * Appended rather than folded into an options bag on purpose: 43 call
   * sites pass positionally today, and an optional trailing parameter
   * churns none of them.
   */
  mapFacing?: VehicleFacing | null,
): Target {
  const base = projectDefender(c);
  if (!derivedCover && !surface && !mapFacing) return base;
  const reading = readMap(c, base, { derivedCover, surface, coverMode, mapFacing });
  if (changesNothing(reading)) return base;
  return derivedFor(base, readingKey(reading), () => wearReading(base, reading));
}

/**
 * The derived Target for `key`, built once per base projection.
 * Memoised so an unchanged derivation returns the same object and
 * nothing downstream rerenders.
 */
function derivedFor(base: Target, key: string, build: () => Target): Target {
  let byKey = derivedProjectionCache.get(base);
  if (!byKey) {
    byKey = new Map();
    derivedProjectionCache.set(base, byKey);
  }
  const hit = byKey.get(key);
  if (hit) return hit;
  const out = build();
  byKey.set(key, out);
  return out;
}

// ────────────────────────────────────────────────────────────
// Attacker projection
// ────────────────────────────────────────────────────────────

/**
 * The combatant's weapons in rack order.
 *
 * Their stored arrangement first - so a weapon dragged to the top stays at
 * the top - minus any the sheet no longer has, plus any the sheet gained
 * elsewhere (character editor, loadout assignment) appended in sheet order.
 * That is what makes "edit the sheet → it appears in the Armoury" true
 * without the rack reshuffling under the user.
 *
 * Tolerant of the pre-8.189.0 SPARSE shape - a 40-long array of ids and
 * nulls - because filtering the nulls out IS the migration. It is also the
 * mixed-version wire format: an older client still writes forty cells.
 *
 * A repeated id is dropped rather than projected twice: two plates sharing
 * a weapon id would share one ammo ledger.
 */
function loadoutOrder(c: Combatant): string[] {
  const weapons = c.sheet.weapons ?? [];
  const ids = new Set(weapons.map((w) => w.id));
  const stored = (c.slotOrder ?? []).filter((id): id is string => !!id && ids.has(id));
  // A Set keeps the FIRST place each id appears: the stored order, then
  // whatever the sheet gained, each once.
  return [...new Set([...stored, ...weapons.map((w) => w.id)])];
}

/**
 * Combatant → the weapon rack, with LIVE ammo overlaid.
 *
 * Exactly as long as the character is armed: no padding, no 40-weapon
 * ceiling, and no empty plates. resolveCombatWeapon loads every weapon full
 * (correct at spawn, wrong mid-combat), so state.weaponAmmo wins.
 *
 * `weaponIds` is the parallel lookup from rack position back to the sheet
 * weapon that produced it - that is how an ammo deduction knows which weapon
 * to decrement. Every entry is a real id; `Slot.weaponId` carries the same
 * value on the slot itself, which is what makes reordering safe.
 */
export function projectLoadout(c: Combatant): { slots: Slot[]; weaponIds: string[] } {
  // Keyed on the EFFECTIVE sheet: a stance changes what a plate shows (a skill, a damage bonus),
  // and that sheet is one stable object per (sheet, stances up), so the key still survives a wound tick.
  const sheet = effectiveSheet(c);
  const hit = loadoutProjectionCache.get(sheet);
  if (hit && sameLoadoutInputs(hit, c)) return hit.out;

  const out = buildLoadout(c);
  loadoutProjectionCache.set(sheet, {
    weaponAmmo: c.state.weaponAmmo,
    slotOrder: c.slotOrder,
    conditions: c.state.conditions,
    out,
  });
  return out;
}

/** Is the cached rack still made of exactly these parts? */
function sameLoadoutInputs(hit: LoadoutEntry, c: Combatant): boolean {
  return (
    hit.weaponAmmo === c.state.weaponAmmo && hit.slotOrder === c.slotOrder && hit.conditions === c.state.conditions
  );
}

/**
 * The rack, built fresh.
 *
 * Stat drains reach the drained side's OWN weapons (v8.290.0, narrowed
 * v8.294.0): Nullifying bites the slot's psy rating and Sapping its
 * Strength bonus - both raw damage arithmetic. skillValue is NOT
 * touched: the attack roll already picks the drain up through
 * `getAttackConditionModifiers`, and doing both subtracted it twice.
 * The cache key keeps `state.conditions` identity for these two.
 */
function buildLoadout(c: Combatant): { slots: Slot[]; weaponIds: string[] } {
  const drains = readDrains(c.state.conditions);
  const slots: Slot[] = [];
  const weaponIds: string[] = [];
  for (const weaponId of loadoutOrder(c)) {
    const resolved = cachedCombatWeapon(effectiveSheet(c), weaponId);
    if (!resolved) continue;
    // Position, taken from the rack as built - so `id` and the array index
    // cannot disagree even if a weapon above failed to resolve.
    slots.push(rackSlot(resolved, { weaponId, position: slots.length, live: c.state.weaponAmmo?.[weaponId], drains }));
    weaponIds.push(weaponId);
  }
  return { slots, weaponIds };
}

interface RackPlace {
  weaponId: string;
  position: number;
  live: CombatWeapon['ammo'] | undefined;
  drains: ReturnType<typeof readDrains>;
}

/** One plate of the rack: the resolved weapon, placed, with live ammo and drains. */
function rackSlot(resolved: CombatWeapon, { weaponId, position, live, drains }: RackPlace): Slot {
  return {
    ...resolved,
    id: position,
    weaponId,
    name: '',
    ammo: live ? { ...live } : { ...resolved.ammo },
    // Sapping is typed in Strength BONUS points, which is exactly what
    // melee damage is paid in - the number as given (v8.296.0).
    ...(drains.str > 0 ? { strengthBonus: Math.max(0, (resolved.strengthBonus || 0) - drains.str) } : {}),
    ...(drains.psy > 0 ? { psyRating: Math.max(0, (resolved.psyRating || 0) - drains.psy) } : {}),
  } as unknown as Slot;
}

/**
 * Attacker stats for the weapon in `weaponIndex`.
 *
 * These are weapon-dependent (WS vs BS, that characteristic's unnatural
 * ×N, and effective Righteous Fury), so they are resolved for the
 * SELECTED weapon. The pre-roster bridge resolved them once from the
 * character's FIRST weapon and reused that for every slot - see
 * dev-docs/wip.md for the note on that latent inconsistency.
 *
 * A null/empty index falls back to the character's base stats, which is
 * what the Overview sheet needs to show WS/BS with no weapon selected.
 */
export function projectAttacker(c: Combatant, weaponIndex: number | null): AttackerCombatStats {
  const sheet = effectiveSheet(c);
  if (weaponIndex === null || weaponIndex < 0) return cachedAttackerStats(sheet, '');
  const { weaponIds } = projectLoadout(c);
  return cachedAttackerStats(sheet, weaponIds[weaponIndex] ?? '');
}
